import type { SyncStatus } from '@awwyeah/shared';

interface ConnectionState {
  connected: boolean;
  synced: boolean;
}

/**
 * Connection-level sync status (ROADMAP WS-1): one badge for the whole app, not one per
 * doc. Rules, in order:
 *
 *   the browser says it is offline               → 'offline'
 *   nothing is open yet                          → 'synced'   (nothing to sync)
 *   no socket is up                              → 'offline'  (server unreachable)
 *   every open doc is connected *and* confirmed  → 'synced'
 *   otherwise                                    → 'syncing'
 *
 * "Offline" therefore means "your edits are safe locally but the family can't see them
 * yet", which is exactly what the user needs to know.
 */
export class SyncStatusStore {
  private readonly connections = new Map<string, ConnectionState>();
  private readonly listeners = new Set<() => void>();
  private online = true;
  private status: SyncStatus = 'synced';
  private detachOnlineEvents: (() => void) | null = null;

  constructor(navigatorLike: { onLine?: boolean } | undefined = globalThis.navigator) {
    this.online = navigatorLike?.onLine ?? true;
  }

  /** Wires `online`/`offline` events; returns a detach function. */
  listenToNetwork(target: EventTarget | undefined = globalThis.window): () => void {
    if (!target) return () => undefined;
    const onOnline = (): void => this.setOnline(true);
    const onOffline = (): void => this.setOnline(false);
    target.addEventListener('online', onOnline);
    target.addEventListener('offline', onOffline);
    this.detachOnlineEvents = () => {
      target.removeEventListener('online', onOnline);
      target.removeEventListener('offline', onOffline);
    };
    return this.detachOnlineEvents;
  }

  setOnline(online: boolean): void {
    this.online = online;
    this.recompute();
  }

  isOnline(): boolean {
    return this.online;
  }

  report(key: string, state: ConnectionState): void {
    this.connections.set(key, state);
    this.recompute();
  }

  forget(key: string): void {
    this.connections.delete(key);
    this.recompute();
  }

  getStatus = (): SyncStatus => this.status;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  destroy(): void {
    this.detachOnlineEvents?.();
    this.detachOnlineEvents = null;
    this.connections.clear();
    this.listeners.clear();
  }

  private compute(): SyncStatus {
    if (!this.online) return 'offline';
    const states = [...this.connections.values()];
    if (states.length === 0) return 'synced';
    if (!states.some((s) => s.connected)) return 'offline';
    return states.every((s) => s.connected && s.synced) ? 'synced' : 'syncing';
  }

  private recompute(): void {
    const next = this.compute();
    if (next === this.status) return;
    this.status = next;
    for (const listener of this.listeners) listener();
  }
}
